import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { generateRosterXlsx } from '../../api/admin';
import './RosterExportModal.css';

const RosterExportModal = ({ programIds, onClose }) => {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const doExport = async () => {
      try {
        const response = await generateRosterXlsx(programIds);
        if (response.status === 'success') {
          setResult(response.data);
          setStatus('success');
        } else {
          setErrorMessage(response.message || 'Failed to generate roster XLSX');
          setStatus('error');
        }
      } catch (error) {
        setErrorMessage(error.message || 'An unexpected error occurred');
        setStatus('error');
      }
    };
    doExport();
  }, [programIds]);

  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    setResult(null);
    const doExport = async () => {
      try {
        const response = await generateRosterXlsx(programIds);
        if (response.status === 'success') {
          setResult(response.data);
          setStatus('success');
        } else {
          setErrorMessage(response.message || 'Failed to generate roster XLSX');
          setStatus('error');
        }
      } catch (error) {
        setErrorMessage(error.message || 'An unexpected error occurred');
        setStatus('error');
      }
    };
    doExport();
  };

  const renderLoading = () => (
    <div className="roster-export-content">
      <div className="loading-spinner"></div>
      <h3>Generating XLSX...</h3>
      <p>Please wait while we generate the roster XLSX for {programIds.length} program{programIds.length > 1 ? 's' : ''}.</p>
    </div>
  );

  const downloadXlsxFile = (filename, base64Content) => {
    // Decode base64 to binary
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAll = () => {
    result.files.forEach((file) => {
      if (file.fileContent) {
        downloadXlsxFile(file.filename, file.fileContent);
      } else if (file.downloadUrl) {
        window.open(file.downloadUrl, '_blank');
      }
    });
  };

  const renderSuccess = () => {
    const { files } = result;
    const isSingleFile = files.length === 1;
    const hasLocalContent = files.some((f) => f.fileContent);

    return (
      <div className="roster-export-content">
        <div className="success-icon">&#10003;</div>
        <h3>Roster XLSX{!isSingleFile ? ' Files' : ''} Generated Successfully</h3>

        {isSingleFile ? (
          <div className="result-details">
            <p><strong>File:</strong> {files[0].filename}</p>
            <p><strong>Programs:</strong> {files[0].programCount}</p>
            <p><strong>Participants:</strong> {files[0].kidCount}</p>
          </div>
        ) : (
          <div className="result-details">
            <p><strong>Files created:</strong> {files.length}</p>
            <ul className="files-list">
              {files.map((file, index) => (
                <li key={index}>
                  {file.filename} ({file.programCount} program{file.programCount > 1 ? 's' : ''}, {file.kidCount} participant{file.kidCount > 1 ? 's' : ''})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="modal-actions">
          {hasLocalContent ? (
            <button className="primary-button" onClick={handleDownloadAll}>
              Download XLSX{!isSingleFile ? ' Files' : ''}
            </button>
          ) : (
            files.map((file, index) => (
              <a
                key={index}
                href={file.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-button"
              >
                Download {file.filename}
              </a>
            ))
          )}
          <button className="secondary-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  };

  const renderError = () => (
    <div className="roster-export-content">
      <div className="error-icon">&#10007;</div>
      <h3>Export Failed</h3>
      <p className="error-message">{errorMessage}</p>
      <div className="modal-actions">
        <button className="primary-button" onClick={handleRetry}>
          Retry
        </button>
        <button className="secondary-button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={status !== 'loading' ? onClose : undefined}
      shouldCloseOnOverlayClick={status !== 'loading'}
      contentLabel="Roster Export"
      ariaHideApp={false}
      className="roster-export-modal-content"
      overlayClassName="modal-overlay"
    >
      <div className="roster-export-modal">
        {status !== 'loading' && (
          <button className="close-button" onClick={onClose}>x</button>
        )}
        {status === 'loading' && renderLoading()}
        {status === 'success' && renderSuccess()}
        {status === 'error' && renderError()}
      </div>
    </ReactModal>
  );
};

export default RosterExportModal;
